import { DestroyRef, Signal, computed, inject, linkedSignal, untracked } from '@angular/core'
import { rxResource, toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute } from '@angular/router'

import {
  patchState,
  signalStore,
  withComputed,
  withLinkedState,
  withMethods,
  withProps,
} from '@ngrx/signals'

import { AsyncSubject, EMPTY, Observable, defer, finalize, map, of, share, tap } from 'rxjs'

import { downloadBlob, jsonToBlob } from '@flowx/shared/utils'

import { FlxThemeApiService } from './theme-api.service'
import { Property, PropertyCollection, Theme } from './theme-model'
import { ColorTokens } from './theme-model/colors'
import { ShadowTokens } from './theme-model/shadows'
import {
  ThemePropertiesModifiersIconsUpdate,
  copyDataPropertiesBetweenPaths,
  copyDataPropertiesFromPathToPlatform,
  copySectionToPlatform,
  getColorOptions,
  getPropertyValue,
  getPropertyWithCalculatedValues,
  getShadowOptions,
  getStyleForProperties,
  getThemeDataFromPath,
  getThemePathForPlatform,
  getTypographyOptions,
  mergeSectionBetweenPaths,
  resetPropertiesAtPath,
  resetSpecificPropertiesAtPath,
  updateThemeAtPath,
  updateThemePropertiesModifiersIconsAtPath,
} from './theme-model/theme.helpers'
import { TypographyTokens } from './theme-model/typography'

import { DISPLAY_MODES, DisplayMode, Platform } from '../model/platform.model'
import {
  MOBILE_ONLY_SECTIONS,
  Section,
  SectionComponent,
  WEB_ONLY_SECTIONS,
} from '../model/sections.model'

/**
 * The theme paths changed since the last save, each with the revision of its latest change. Paths
 * never overlap, so the PATCH entries built from them can be applied in any order.
 */
export type PendingUpdates = Record<string, number>

/** How long the previews trail the edits, so typing does not rebuild them on every keystroke. */
export const PREVIEW_DEBOUNCE = 300

interface SaveRequest {
  readonly sent: PendingUpdates
}

type SectionPath = Section | SectionComponent | string

const VALID_DISPLAY_MODES: readonly string[] = DISPLAY_MODES.map(({ value }) => value)

const isWithin = (path: string, ancestor: string): boolean =>
  path === ancestor || path.startsWith(`${ancestor}.`) || path.startsWith(`${ancestor}[`)

/**
 * Records changed paths at a revision, keeping the pending paths disjoint: a path under a pending
 * one bumps that ancestor instead, and a path above pending ones replaces them.
 */
export const addPendingPaths = (
  pending: PendingUpdates,
  paths: readonly string[],
  revision: number
): PendingUpdates =>
  paths.reduce((current, path) => {
    const ancestor = Object.keys(current).find((pendingPath) => isWithin(path, pendingPath))
    return ancestor
      ? { ...current, [ancestor]: revision }
      : {
          ...Object.fromEntries(
            Object.entries(current).filter(([pendingPath]) => !isWithin(pendingPath, path))
          ),
          [path]: revision,
        }
  }, pending)

/**
 * The platform the editor shows. The URL keeps the platform the user picked; a mobile-only or a
 * web-only section overrides it without rewriting the URL, so leaving that section returns to the
 * user's choice.
 */
export const coercePlatform = (
  section: SectionPath | null,
  urlPlatform: Platform | null
): Platform => {
  if (MOBILE_ONLY_SECTIONS.includes(section)) {
    return urlPlatform && urlPlatform !== Platform.Web ? urlPlatform : Platform.Android
  }
  if (WEB_ONLY_SECTIONS.includes(section)) {
    return Platform.Web
  }
  return urlPlatform ?? Platform.Web
}

/** Display modes only exist on web; mobile platforms have a single, unprefixed display mode. */
export const coerceDisplayMode = (
  platform: Platform,
  urlDisplayMode: string | null
): DisplayMode =>
  platform === Platform.Web && VALID_DISPLAY_MODES.includes(urlDisplayMode)
    ? (urlDisplayMode as DisplayMode)
    : DisplayMode.FILL

/**
 * State of the theme editor page: the loaded theme, the working copy being edited, the pending
 * updates that Save sends, and the platform, display mode and section derived from the URL.
 * Provided by the theme page, so it lives exactly as long as the page.
 *
 * Edits reach the working copy at once; the previews follow `previewTheme`, which trails field
 * edits by {@link PREVIEW_DEBOUNCE} and catches up at once on reset, copy and theme load.
 */
export const ThemeEditorStore = signalStore(
  withProps(() => {
    const route = inject(ActivatedRoute)
    const paramMap = toSignal(route.paramMap, { requireSync: true })
    const queryParamMap = toSignal(route.queryParamMap, { requireSync: true })
    return {
      _api: inject(FlxThemeApiService),
      themeId: computed(() => paramMap().get('id')),
      section: computed(() => queryParamMap().get('section') as Section | null),
      _urlPlatform: computed(() => queryParamMap().get('platform') as Platform | null),
      _urlDisplayMode: computed(() => queryParamMap().get('displayMode')),
    }
  }),
  withProps((store) => ({
    loadedTheme: rxResource({
      params: () => store.themeId() ?? undefined,
      stream: ({ params: id }) => store._api.getTheme(id),
    }),
  })),
  // The working copy, its preview, the pending updates and the running save derive from the loaded
  // theme: edits write them, and loading another theme resets them all.
  withLinkedState(({ loadedTheme }) => {
    const loaded = computed<Theme | null>(() =>
      loadedTheme.hasValue() ? loadedTheme.value() : null
    )
    return {
      theme: loaded,
      previewTheme: loaded,
      pending: linkedSignal<Theme | null, PendingUpdates>({
        source: loaded,
        computation: () => ({}),
      }),
      _saveRequest: linkedSignal<Theme | null, SaveRequest | null>({
        source: loaded,
        computation: () => null,
      }),
    }
  }),
  withComputed(({ theme, pending, _saveRequest, section, _urlPlatform, _urlDisplayMode }) => {
    const platform = computed(() => coercePlatform(section(), _urlPlatform()))
    const dataAt = <T>(path: string): T | null =>
      getThemeDataFromPath<T>(theme(), getThemePathForPlatform(path, platform()))
    const colors = computed(() => dataAt<ColorTokens>(Section.Color))
    const typography = computed(() => dataAt<TypographyTokens>(Section.Typography))
    const shadows = computed(() => dataAt<ShadowTokens>(Section.Shadows))
    return {
      platform,
      displayMode: computed(() => coerceDisplayMode(platform(), _urlDisplayMode())),
      hasUpdates: computed(() => Object.keys(pending()).length > 0),
      isSaving: computed(() => _saveRequest() !== null),
      colorOptions: computed(() => (colors() ? getColorOptions(colors()) : [])),
      typographyOptions: computed(() => (typography() ? getTypographyOptions(typography()) : [])),
      shadowOptions: computed(() => (shadows() ? getShadowOptions(shadows()) : [])),
    }
  }),
  withMethods((store) => {
    const fullPath = (path: SectionPath, platform: Platform = store.platform()): string =>
      getThemePathForPlatform(path, platform)

    let revision = 0
    let previewTimer: ReturnType<typeof setTimeout> | undefined
    /** Observes completion without subscribing to the HTTP request. */
    let saveSettled: Observable<void> = of(undefined)
    inject(DestroyRef).onDestroy(() => clearTimeout(previewTimer))

    /**
     * Stores the new working copy and records the changed paths in the pending updates. A field
     * edit reaches the previews after {@link PREVIEW_DEBOUNCE}; any other change reaches them at once.
     */
    const commit = (
      updatedTheme: Theme,
      paths: readonly string[],
      preview: 'debounced' | 'immediate' = 'immediate'
    ): void => {
      revision += 1
      clearTimeout(previewTimer)
      patchState(store, {
        theme: updatedTheme,
        pending: addPendingPaths(store.pending(), paths, revision),
        ...(preview === 'immediate' ? { previewTheme: updatedTheme } : {}),
      })
      if (preview === 'debounced') {
        previewTimer = setTimeout(
          () => patchState(store, { previewTheme: store.theme() }),
          PREVIEW_DEBOUNCE
        )
      }
    }

    const propertyValue = (property?: Property): string | null => {
      if (!property) {
        return null
      }
      const resolved =
        property.value === undefined && property.token === undefined
          ? getPropertyWithCalculatedValues(store.theme(), store.platform(), property)
          : property
      return getPropertyValue(resolved)
    }

    return {
      /** The theme data at a section path, for the current platform. Tracked inside `computed`. */
      dataAt<T>(path: SectionPath): T | null {
        return getThemeDataFromPath<T>(store.theme(), fullPath(path))
      },
      updateThemeDataAtPath<T>(path: string, newData: T, section?: SectionPath): void {
        const updatePath = fullPath(path)
        commit(
          updateThemeAtPath<T>(store.theme(), updatePath, newData),
          [section ? fullPath(section) : updatePath],
          'debounced'
        )
      },
      updateThemePropertiesModifiersIconsAtPath(
        path: string,
        properties?: ThemePropertiesModifiersIconsUpdate['properties'],
        modifiers?: ThemePropertiesModifiersIconsUpdate['modifiers'],
        icons?: ThemePropertiesModifiersIconsUpdate['icons']
      ): void {
        commit(
          updateThemePropertiesModifiersIconsAtPath(store.theme(), store.platform(), path, {
            properties,
            modifiers,
            icons,
          }),
          [fullPath(path)],
          'debounced'
        )
      },
      copySectionAtPath(fromPlatform: Platform, toPlatform: Platform, section: SectionPath): void {
        commit(copySectionToPlatform(store.theme(), fromPlatform, toPlatform, section), [
          fullPath(section, toPlatform),
        ])
      },
      copySectionAtPaths(fromPlatform: Platform, toPlatform: Platform, paths: SectionPath[]): void {
        commit(
          paths.reduce(
            (theme, path) => copySectionToPlatform(theme, fromPlatform, toPlatform, path),
            store.theme()
          ),
          paths.map((path) => fullPath(path, toPlatform))
        )
      },
      copyPropertiesAtPath(
        properties: readonly string[] | string[] | null,
        fromPlatform: Platform,
        toPlatform: Platform,
        path: SectionPath
      ): void {
        commit(
          copyDataPropertiesFromPathToPlatform(
            store.theme(),
            fromPlatform,
            toPlatform,
            path,
            properties ?? undefined
          ),
          [fullPath(path, toPlatform)]
        )
      },
      copyPropertiesBetweenPaths(
        properties: readonly string[] | string[],
        fromPath: string,
        toPath: string
      ): void {
        commit(
          copyDataPropertiesBetweenPaths(
            store.theme(),
            store.platform(),
            fromPath,
            toPath,
            properties
          ),
          [fullPath(toPath)]
        )
      },
      mergeSectionBetweenPaths<T extends Record<string, unknown>>(
        fromPath: string,
        toPath: string
      ): void {
        commit(mergeSectionBetweenPaths<T>(store.theme(), fullPath(fromPath), fullPath(toPath)), [
          fullPath(toPath),
        ])
      },
      resetAtPath(path: string): void {
        commit(resetPropertiesAtPath(store.theme(), store.platform(), path), [fullPath(path)])
      },
      resetAtPaths(paths: string[]): void {
        commit(
          paths.reduce(
            (theme, path) => resetPropertiesAtPath(theme, store.platform(), path),
            store.theme()
          ),
          paths.map((path) => fullPath(path))
        )
      },
      resetSpecificPropertiesAtPath(path: string, propertyNames: readonly string[]): void {
        commit(
          resetSpecificPropertiesAtPath(store.theme(), store.platform(), path, propertyNames),
          [fullPath(path)]
        )
      },
      /**
       * A computed over the preview data of a section: it recomputes when `previewTheme`, the
       * platform or the display mode changes, never in between, and reads everything else untracked.
       */
      previewComputed<T>(compute: () => T): Signal<T> {
        return computed(() => {
          store.previewTheme()
          store.platform()
          store.displayMode()
          return untracked(compute)
        })
      },
      /** The effective value of a property: its override, else its (inherited) default. */
      getPropertyValue: propertyValue,
      getStyleForProperties(
        properties: Record<string, Property> | Property[]
      ): Record<string, string> {
        return getStyleForProperties(store.theme(), store.platform(), properties as Property[])
      },
      getFormGroupEntriesForProperties<T extends readonly string[]>(
        propsSubset: T,
        properties: PropertyCollection
      ): Record<T[number], string | null> {
        return Object.fromEntries(
          propsSubset.map((prop) => [prop, propertyValue(properties[prop])])
        ) as Record<T[number], string | null>
      },
      getIconSettings(iconProps: Record<string, Property>): Record<string, string | null> {
        return Object.fromEntries(
          Object.entries(iconProps).map(([key, value]) => [key, propertyValue(value)])
        )
      },
      /**
       * Sends the pending updates, built from the working copy, one save at a time. On success only
       * the paths unchanged since the request started are cleared, so an edit made while it was in
       * flight stays pending; on failure everything stays pending. A response for a theme that is no
       * longer loaded is ignored. A save starts on its first subscription and cannot be restarted
       * by resubscribing; retrying requires a new call to save().
       */
      save(): Observable<unknown> {
        return defer(() => {
          if (store.isSaving() || !store.hasUpdates()) {
            return EMPTY
          }
          const theme = store.theme()
          const request: SaveRequest = { sent: store.pending() }
          const body = Object.keys(request.sent).map((path) => ({
            path,
            value: getThemeDataFromPath(theme, path),
          }))
          const settled = new AsyncSubject<void>()
          saveSettled = settled.asObservable()
          const settle = (succeeded: boolean): void => {
            if (store._saveRequest() !== request) {
              return
            }
            patchState(store, {
              _saveRequest: null,
              ...(succeeded
                ? {
                    pending: Object.fromEntries(
                      Object.entries(store.pending()).filter(
                        ([path, pathRevision]) => request.sent[path] !== pathRevision
                      )
                    ),
                  }
                : {}),
            })
          }
          patchState(store, { _saveRequest: request })
          let receivedResponse = false
          return defer(() => store._api.updateTheme(theme.flowxUuid, body)).pipe(
            tap({
              next: () => (receivedResponse = true),
              complete: () => settle(receivedResponse),
              error: () => settle(false),
            }),
            finalize(() => {
              settle(false)
              settled.next()
              settled.complete()
            })
          )
        }).pipe(
          // Once started, let the write finish even if its observers leave. Never resend its body.
          share({ resetOnError: false, resetOnComplete: false, resetOnRefCountZero: false })
        )
      },
      /** Emits once no save of this theme is running. */
      whenSaved(): Observable<void> {
        return store.isSaving() ? saveSettled : of(undefined)
      },
      updateThemeName(name: Theme['name']): Observable<unknown> {
        return store._api
          .updateThemeName(store.theme().flowxUuid, name)
          .pipe(tap(() => patchState(store, { theme: { ...store.theme(), name } })))
      },
      exportTheme(): Observable<{ entityName: string; themeBlob: Blob }> {
        return store._api.export(store.theme().flowxUuid).pipe(
          map((entity) => ({ entityName: entity.theme.name, themeBlob: jsonToBlob(entity) })),
          tap(({ entityName, themeBlob }) =>
            downloadBlob(themeBlob, `${entityName}_${new Date().toISOString()}.json`)
          )
        )
      },
    }
  })
)

export type ThemeEditorStore = InstanceType<typeof ThemeEditorStore>
